import React, { useState, useMemo } from "react";
import { Box, Typography, Card, Container, useTheme } from "@mui/material";
import AccountingTable from "./AccountingTable";
import { Helmet } from "react-helmet-async";
import { findFund, findFundAbbById, findStrategy } from "../../component/Methods";
import AccountingDialog from "./AccountingDialog";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { useAccountingState } from "../../hooks/useOptimizedState";

const Accounting = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);

  const { accountingData, funds, strategies } = useAccountingState();

  // Process accounting data similar to the original implementation
  const processedData = useMemo(() => {
    const fund = accountingData?.[0]?.funds;
    if (!fund) return [];

    return fund.map((row) => {
      const foundFund = findFund(row?.fund, funds);
      const strategyAUMsData = Object.keys(row?.strategyAUMs || {})?.map(
        (strategyId) => {
          return {
            strategyName: findStrategy(strategyId, strategies),
            strategyAUM: row?.strategyAUMs[strategyId],
            strategyId: strategyId,
          };
        }
      );

      const filteredStrategyAUMsData = strategyAUMsData?.filter(
        (item) => item?.strategyName && item?.strategyAUM !== undefined
      );

      const sortedStrategyAUMsData = filteredStrategyAUMsData
        ?.slice()
        ?.sort((a, b) => a?.strategyName?.localeCompare(b?.strategyName));

      return {
        ...row,
        name: foundFund,
        abbreviation: findFundAbbById(row?.fund, funds) ?? row?.abbreviation ?? "-",
        strategyAUMs: sortedStrategyAUMsData,
        id: row?.fund || Math.random().toString(),
      };
    });
  }, [accountingData, funds, strategies]);

  const handleEdit = (record) => {
    setRecordForEdit(record);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setRecordForEdit(null);
  };

  const handleSync = () => {
    // Implement sync functionality - request fresh accounting data
    const syncRequest = {
      action: "get_accounting_system",
    };

    sendWebSocketMessage(JSON.stringify(syncRequest));
  };

  return (
    <>
      <Helmet>
        <title>Accounting | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Accounting
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Fund accounting overview with strategy allocations
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <AccountingTable
              accountingData={processedData}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              onEdit={handleEdit}
              onSync={handleSync}
            />
          </Card>
        </Container>

        {/* Edit Dialog */}
        <AccountingDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          record={recordForEdit}
        />
      </Box>
    </>
  );
};

export default Accounting;
